import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Clapperboard, Plus, Trash2, Headphones, Zap } from "lucide-react";
import { API } from "../../lib/adventureApi";
import { authHeader } from "../../lib/auth";

const EMPTY = { hero_video: "", arrival_video: "", culture_video: "", food_video: "", films: [], articles: [] };

export default function TourMediaAdmin() {
  const [tours, setTours] = useState([]);
  const [slug, setSlug] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prewarming, setPrewarming] = useState(false);

  useEffect(() => {
    fetch(`${API}/tours`).then((r) => r.json()).then((d) => {
      const list = Array.isArray(d) ? d : d.tours || [];
      setTours(list); if (list[0]) setSlug(list[0].slug);
    }).catch(() => toast.error("Could not load tours"));
  }, []);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API}/admin/tour-media/${slug}`, { headers: authHeader() })
      .then((r) => r.json())
      .then((d) => setForm({ ...EMPTY, ...d, films: d.films || [], articles: d.articles || [] }))
      .catch(() => setForm(EMPTY))
      .finally(() => setLoading(false));
  }, [slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRow = (key, i, field, v) => setForm((f) => {
    const arr = [...f[key]]; arr[i] = { ...arr[i], [field]: v }; return { ...f, [key]: arr };
  });
  const addRow = (key, row) => setForm((f) => ({ ...f, [key]: [...f[key], row] }));
  const delRow = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, x) => x !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/tour-media/${slug}`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Media saved", { description: "Live on the tour's experience instantly." });
    } catch (_) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const prewarm = async () => {
    setPrewarming(true);
    try {
      const res = await fetch(`${API}/admin/tours/${slug}/podcast/prewarm`, { method: "POST", headers: authHeader() });
      const d = await res.json();
      toast.success(d.status === "ready" ? "Podcast already ready" : "Podcast generating…", { description: d.status === "ready" ? "Plays instantly." : "Takes ~30–60s, then it's saved." });
    } catch (_) { toast.error("Could not start"); } finally { setPrewarming(false); }
  };

  const prewarmFlagships = async () => {
    try {
      const res = await fetch(`${API}/admin/podcasts/prewarm-flagships`, { method: "POST", headers: authHeader() });
      const d = await res.json();
      toast.success(`Prewarming ${d.started?.length || 0} flagship podcasts`);
    } catch (_) { toast.error("Could not start"); }
  };

  const vidField = (key, label, hint) => (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-ink-muted">{label}</label>
      <input data-testid={`media-${key}`} value={form[key] || ""} onChange={(e) => set(key, e.target.value)}
        placeholder={hint}
        className="w-full mt-1 h-10 px-3 rounded-sm border border-sand-dark text-ink text-sm font-mono focus:border-moss outline-none" />
    </div>
  );

  return (
    <div data-testid="tour-media-admin" className="max-w-3xl">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 bg-moss rounded-sm flex items-center justify-center"><Clapperboard className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="font-display font-extrabold text-xl tracking-tight leading-none">Tour Media &amp; Experience</h2>
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-1">Pin exact videos, articles &amp; podcast per tour</p>
        </div>
      </div>
      <p className="text-sm text-ink-secondary mt-3 mb-5">Leave a field blank to use the smart auto-match. Paste a YouTube ID or full URL. Changes apply to the experience modal instantly.</p>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex-1 min-w-[240px]">
          <label className="text-xs font-mono uppercase tracking-widest text-ink-muted">Tour</label>
          <select data-testid="media-tour-select" value={slug} onChange={(e) => setSlug(e.target.value)}
            className="w-full mt-1 h-11 px-3 rounded-sm border border-sand-dark bg-white text-ink text-sm focus:border-moss outline-none">
            {tours.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
          </select>
        </div>
        <button onClick={prewarmFlagships} data-testid="prewarm-flagships-btn"
          className="flex items-center gap-2 border border-sand-dark hover:border-terracotta h-11 px-4 rounded-sm text-sm font-medium transition-colors">
          <Zap className="w-4 h-4 text-terracotta" /> Prewarm all flagships
        </button>
      </div>

      {loading ? (
        <div className="flex items-center text-ink-secondary"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading…</div>
      ) : (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            {vidField("hero_video", "Hero / Films film", "auto")}
            {vidField("arrival_video", "Arrival (landing) video", "auto — e.g. Lukla / KTM")}
            {vidField("culture_video", "Culture video", "auto")}
            {vidField("food_video", "Food video", "auto")}
          </div>

          {/* Films list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-mono uppercase tracking-widest text-ink-muted">Films tab (overrides auto-match)</label>
              <button onClick={() => addRow("films", { id: "", title: "" })} data-testid="add-film"
                className="flex items-center gap-1 text-xs text-terracotta hover:underline"><Plus className="w-3.5 h-3.5" /> Add film</button>
            </div>
            <div className="space-y-2">
              {form.films.map((f, i) => (
                <div key={i} className="flex gap-2">
                  <input value={f.id || ""} onChange={(e) => setRow("films", i, "id", e.target.value)} placeholder="YouTube ID/URL"
                    className="w-40 h-9 px-2 rounded-sm border border-sand-dark text-sm font-mono focus:border-moss outline-none" />
                  <input value={f.title || ""} onChange={(e) => setRow("films", i, "title", e.target.value)} placeholder="Title"
                    className="flex-1 h-9 px-2 rounded-sm border border-sand-dark text-sm focus:border-moss outline-none" />
                  <button onClick={() => delRow("films", i)} className="w-9 h-9 flex items-center justify-center text-ink-muted hover:text-terracotta"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {!form.films.length && <p className="text-xs text-ink-muted">Using auto-matched RA films.</p>}
            </div>
          </div>

          {/* Articles list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-mono uppercase tracking-widest text-ink-muted">Journal — authority articles (pinned first)</label>
              <button onClick={() => addRow("articles", { title: "", url: "" })} data-testid="add-article"
                className="flex items-center gap-1 text-xs text-terracotta hover:underline"><Plus className="w-3.5 h-3.5" /> Add article</button>
            </div>
            <div className="space-y-2">
              {form.articles.map((a, i) => (
                <div key={i} className="flex gap-2">
                  <input value={a.title || ""} onChange={(e) => setRow("articles", i, "title", e.target.value)} placeholder="Article title"
                    className="flex-1 h-9 px-2 rounded-sm border border-sand-dark text-sm focus:border-moss outline-none" />
                  <input value={a.url || ""} onChange={(e) => setRow("articles", i, "url", e.target.value)} placeholder="https://…"
                    className="flex-1 h-9 px-2 rounded-sm border border-sand-dark text-sm font-mono focus:border-moss outline-none" />
                  <button onClick={() => delRow("articles", i)} className="w-9 h-9 flex items-center justify-center text-ink-muted hover:text-terracotta"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {!form.articles.length && <p className="text-xs text-ink-muted">Using auto-matched journal posts.</p>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-sand-dark">
            <button onClick={save} disabled={saving} data-testid="media-save"
              className="flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-5 h-11 rounded-sm font-semibold transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save media
            </button>
            <button onClick={prewarm} disabled={prewarming} data-testid="media-prewarm"
              className="flex items-center gap-2 border border-sand-dark hover:border-moss px-5 h-11 rounded-sm font-medium text-sm transition-colors disabled:opacity-60">
              {prewarming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Headphones className="w-4 h-4 text-moss" />} Prewarm this podcast
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
